import {
  Business,
  Category,
  Email,
  Home,
  Map,
  Place,
  Public,
} from '@mui/icons-material';
import {
  Box,
  Chip,
  Grid,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { updateBusinessSummary } from 'lib/api';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { validatePermissions } from 'lib/functions/utils';
import { showAlert } from 'lib/functions/utils';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { User } from 'services/redux/authSlice';

import {
  companySizes,
  economicActivities,
  economicSectors,
  economicSubSectors,
} from '../constants';
import { regionsHierarchy } from '../newLocationConstants';

interface EditorProps {
  setEditing: (editing: boolean) => void;
  user: User;
  setUp: () => Promise<void>;
  businessIdentifier: string;
  email: string | null;
  region: string | null;
  province: string | null;
  commune: string | null;
  address: string | null;
  economicSector: string | null;
  economicSubSector: string | null;
  economicActivity: string | null;
  salesSegment: string | null;
}

const EditRow = ({
  label,
  value,
  setValue,
  icon,
  type = 'text',
  options = [],
  disabled = false,
  placeholder,
  multiline = false,
  rows = 1,
}: {
  label: string;
  value: string;
  setValue: (value: string) => void;
  icon: React.ReactNode;
  type?: 'text' | 'select';
  options?: { name: string }[];
  disabled?: boolean;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
}) => {
  return (
    <Box display="flex" alignItems="flex-start" gap={1} sx={{ mb: 2 }}>
      <Box
        sx={{
          backgroundColor: 'rgba(25, 118, 210, 0.1)',
          borderRadius: '50%',
          p: 0.4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '24px',
          height: '24px',
          mt: 0.5,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: '0.65rem', display: 'block', mb: 0.5 }}
        >
          {label}
        </Typography>
        {type === 'select' ? (
          <Select
            value={value || ''}
            onChange={(e) => setValue(e.target.value)}
            fullWidth
            size="small"
            disabled={disabled}
            displayEmpty
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderRadius: '6px',
              },
              '& .MuiSelect-select': {
                color: value ? 'text.primary' : 'text.disabled',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              },
            }}
          >
            <MenuItem
              value=""
              disabled
              sx={{ color: 'text.disabled', fontStyle: 'italic' }}
            >
              {placeholder ?? `Seleccionar ${label.toLowerCase()}`}
            </MenuItem>
            {options.map((option) => (
              <MenuItem key={option.name} value={option.name}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
        ) : (
          <TextField
            value={value || ''}
            onChange={(e) => setValue(e.target.value)}
            fullWidth
            size="small"
            disabled={disabled}
            placeholder={placeholder ?? `Ingresar ${label.toLowerCase()}`}
            multiline={multiline}
            rows={rows}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderRadius: '6px',
              },
              '& .MuiInputBase-input': {
                color: value ? 'text.primary' : 'text.primary',
                textOverflow: multiline ? 'clip' : 'ellipsis',
                overflow: multiline ? 'auto' : 'hidden',
                whiteSpace: multiline ? 'pre-wrap' : 'nowrap',
              },
              '& .MuiInputBase-input::placeholder': {
                color: 'text.disabled',
                opacity: 1,
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              },
            }}
          />
        )}
      </Box>
    </Box>
  );
};

const Editor: React.FC<EditorProps> = (props) => {
  const {
    setEditing,
    user,
    setUp,
    businessIdentifier,
    email,
    region,
    province,
    commune,
    address,
    economicSector,
    economicSubSector,
    economicActivity,
    salesSegment,
  } = props;

  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [emailInEdition, setEmailInEdition] = useState(email ?? '');
  const [regionInEdition, setRegionInEdition] = useState(region ?? '');
  const [provinceInEdition, setProvinceInEdition] = useState(province ?? '');
  const [communeInEdition, setCommuneInEdition] = useState(commune ?? '');
  const [addressInEdition, setAddressInEdition] = useState(address ?? '');
  const [economicSectorInEdition, setEconomicSectorInEdition] = useState(
    economicSector ?? '',
  );
  const [economicSubSectorInEdition, setEconomicSubSectorInEdition] = useState(
    economicSubSector ?? '',
  );
  const [economicActivityInEdition, setEconomicActivityInEdition] = useState(
    economicActivity ?? '',
  );
  const [salesSegmentInEdition, setSalesSegmentInEdition] = useState(
    salesSegment ?? '',
  );

  const handleSave = async () => {
    setLoading(true);
    try {
      const data = {
        email: emailInEdition,
        region: regionInEdition,
        province: provinceInEdition,
        commune: communeInEdition,
        address: addressInEdition,
        economicSector: economicSectorInEdition,
        economicSubSector: economicSubSectorInEdition,
        economicActivity: economicActivityInEdition,
        salesSegment: salesSegmentInEdition,
      };
      await updateBusinessSummary(dispatch, businessIdentifier, data);
      showAlert(dispatch, 'success', 'Información actualizada correctamente');
      await setUp();
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al actualizar la información');
    }
    setLoading(false);
  };

  if (loading) {
    return <CenteredCircularProgress />;
  }

  const regionOptions = regionsHierarchy.sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  const provincesOptions = regionInEdition
    ? regionsHierarchy
        .filter((region) => region.name === regionInEdition)
        .map((region) => region.provinces)
        .flat()
        .sort((a, b) => a.name.localeCompare(b.name))
    : [];
  const communesOptions = provinceInEdition
    ? regionsHierarchy
        .filter((region) => region.name === regionInEdition)
        .map((region) => region.provinces)
        .flat()
        .filter((province) => province.name === provinceInEdition)
        .map((province) => province.communes)
        .flat()
        .sort((a, b) => a.name.localeCompare(b.name))
    : [];

  const handleRegionChange = (value: string) => {
    setRegionInEdition(value);
    setCommuneInEdition('');
    setProvinceInEdition('');
  };

  const handleProvinceChange = (value: string) => {
    setProvinceInEdition(value);
    setCommuneInEdition('');
  };

  const handleCommuneChange = (value: string) => {
    setCommuneInEdition(value);
  };

  return (
    <Paper
      sx={{
        p: 3,
        boxShadow: 'none',
        border: '1px solid #e0e0e0',
        position: 'relative',
        marginTop: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontSize: '1.2rem',
              lineHeight: 1.1,
            }}
          >
            Editar Información de la Empresa
          </Typography>
          <Chip
            label="Modo Edición"
            size="small"
            color="warning"
            variant="outlined"
            sx={{ height: '22px', fontSize: '0.75rem' }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <CustomButton
            variant="outlined"
            size="small"
            color="secondary"
            onClick={() => setEditing(false)}
          >
            Cancelar
          </CustomButton>

          <CustomButton
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={!validatePermissions(user, 'businesses_create_new')}
          >
            Guardar
          </CustomButton>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                borderRadius: '8px',
                px: 1.5,
                py: 0.8,
                border: '1px solid rgba(25, 118, 210, 0.15)',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: 'primary.main',
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  textAlign: 'center',
                }}
              >
                Identificación
              </Typography>
            </Box>
            <EditRow
              label="RUT"
              value={businessIdentifier}
              setValue={() => {}}
              icon={
                <Business sx={{ fontSize: '0.8rem', color: 'primary.main' }} />
              }
              disabled
            />
            <EditRow
              label="Email"
              value={emailInEdition}
              setValue={setEmailInEdition}
              icon={
                <Email sx={{ fontSize: '0.8rem', color: 'primary.main' }} />
              }
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                backgroundColor: 'rgba(255, 152, 0, 0.08)',
                borderRadius: '8px',
                px: 1.5,
                py: 0.8,
                border: '1px solid rgba(255, 152, 0, 0.15)',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: 'warning.main',
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  textAlign: 'center',
                }}
              >
                Ubicación
              </Typography>
            </Box>
            <EditRow
              label="Región"
              value={regionInEdition}
              setValue={handleRegionChange}
              icon={
                <Public sx={{ fontSize: '0.8rem', color: 'warning.main' }} />
              }
              type="select"
              options={regionOptions}
              placeholder="Seleccionar región"
            />
            <EditRow
              label="Provincia"
              value={provinceInEdition}
              setValue={handleProvinceChange}
              icon={<Map sx={{ fontSize: '0.8rem', color: 'warning.main' }} />}
              type="select"
              options={provincesOptions}
              placeholder="Seleccionar provincia"
            />
            <EditRow
              label="Comuna"
              value={communeInEdition}
              setValue={handleCommuneChange}
              icon={
                <Place sx={{ fontSize: '0.8rem', color: 'warning.main' }} />
              }
              type="select"
              options={communesOptions}
              placeholder="Seleccionar comuna"
            />
            <EditRow
              label="Dirección"
              value={addressInEdition}
              setValue={setAddressInEdition}
              icon={<Home sx={{ fontSize: '0.8rem', color: 'warning.main' }} />}
              placeholder="Av. Principal 123, Santiago"
              multiline
              rows={2}
            />
          </Box>
        </Grid>

        {/* Columna 3: Información Económica */}
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                backgroundColor: 'rgba(76, 175, 80, 0.08)',
                borderRadius: '8px',
                px: 1.5,
                py: 0.8,
                border: '1px solid rgba(76, 175, 80, 0.15)',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: 'success.main',
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  textAlign: 'center',
                }}
              >
                Clasificación Económica
              </Typography>
            </Box>
            <EditRow
              label="Sector Económico"
              value={economicSectorInEdition}
              setValue={setEconomicSectorInEdition}
              icon={
                <Category sx={{ fontSize: '0.8rem', color: 'success.main' }} />
              }
              type="select"
              options={economicSectors}
              placeholder="Seleccionar sector económico"
            />
            <EditRow
              label="Subsector Económico"
              value={economicSubSectorInEdition}
              setValue={setEconomicSubSectorInEdition}
              icon={
                <Category sx={{ fontSize: '0.8rem', color: 'success.main' }} />
              }
              type="select"
              options={economicSubSectors}
              placeholder="Seleccionar subsector económico"
            />
            <EditRow
              label="Actividad Económica"
              value={economicActivityInEdition}
              setValue={setEconomicActivityInEdition}
              icon={
                <Category sx={{ fontSize: '0.8rem', color: 'success.main' }} />
              }
              type="select"
              options={economicActivities}
              placeholder="Seleccionar actividad económica"
            />
            <EditRow
              label="Segmento de Ventas"
              value={salesSegmentInEdition}
              setValue={setSalesSegmentInEdition}
              icon={
                <Business sx={{ fontSize: '0.8rem', color: 'success.main' }} />
              }
              type="select"
              options={companySizes}
              placeholder="Seleccionar segmento de ventas"
            />
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default Editor;
